<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Appointments</h1>
          <p class="lede">Book, check in, complete. Each provider gets a column so you never double-book.</p>
        </div>
        <div class="head-actions">
          <button class="secondary" @click="shiftDay(-1)"><i class="mdi mdi-chevron-left"></i></button>
          <input type="date" v-model="day" @change="load" style="max-width:180px" />
          <button class="secondary" @click="shiftDay(1)"><i class="mdi mdi-chevron-right"></i></button>
          <button class="secondary" @click="jumpToday">Today</button>
          <button class="primary" @click="openBook(null, null)">+ Book appointment</button>
        </div>
      </div>

      <div v-if="!providers.length" class="card muted center" style="padding:60px 24px">
        No service providers yet. In <router-link to="/settings">Settings</router-link>, mark staff as providers to enable bookings.
      </div>

      <div v-else class="calendar card">
        <div class="cal-grid" :style="{ gridTemplateColumns: `70px repeat(${providers.length}, minmax(160px, 1fr))` }">
          <!-- Header row: time gutter + provider names -->
          <div class="cal-header">Time</div>
          <div v-for="p in providers" :key="p.id" class="cal-header">
            <div class="prov-name">{{ p.name }}</div>
            <div class="prov-comm muted small">{{ p.default_commission_percent }}% commission</div>
          </div>

          <!-- Time rows -->
          <template v-for="slot in slots" :key="slot.iso">
            <div class="cal-time">{{ slot.label }}</div>
            <div v-for="p in providers" :key="p.id + '-' + slot.iso"
                 class="cal-cell"
                 :class="{ 'has-appt': hasAppt(p.id, slot) }"
                 @click="onSlotClick(p.id, slot)">
              <div v-for="a in apptsFor(p.id, slot)" :key="a.id"
                   class="appt-block"
                   :class="'st-' + a.status"
                   :style="apptStyle(a)"
                   @click.stop="openAppt(a)">
                <div class="appt-head">
                  <span class="appt-time">{{ time(a.start_at) }} – {{ time(a.end_at) }}</span>
                  <span :class="'appt-pill ' + a.status">{{ a.status.replace(/_/g,' ') }}</span>
                </div>
                <div class="appt-service">{{ a.service_name_snapshot }}</div>
                <div class="appt-cust muted small">
                  <i class="mdi mdi-account-outline"></i> {{ a.customer_name || a.guest_name || 'Guest' }}
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- BOOKING modal -->
    <div v-if="showBook" class="modal-back" @click.self="showBook=false">
      <div class="modal card">
        <h3>Book appointment</h3>
        <label>Service</label>
        <select v-model.number="book.serviceVariantId">
          <option :value="0" disabled>-- pick a service --</option>
          <option v-for="s in services" :key="s.variant_id" :value="s.variant_id">
            {{ s.product_name }} · {{ Math.round(s.duration_minutes || 30) }} min · KES {{ format(s.sell_price) }}
          </option>
        </select>
        <label>Provider</label>
        <select v-model.number="book.providerUserId">
          <option :value="0" disabled>-- pick a provider --</option>
          <option v-for="p in providers" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <div class="row-2">
          <div>
            <label>Date</label>
            <input type="date" v-model="book.date" />
          </div>
          <div>
            <label>Start time</label>
            <input type="time" v-model="book.time" />
          </div>
        </div>
        <label>Customer (existing)</label>
        <div class="cust-picker">
          <input v-model="custSearch" @input="onCustSearch" placeholder="Search by name or phone…" />
          <div v-if="custResults.length" class="cust-results">
            <div v-for="c in custResults" :key="c.id" class="cust-result" @click="pickCustomer(c)">
              <strong>{{ c.name || 'Unnamed' }}</strong> <span class="muted small">· {{ c.phone || 'No phone' }}</span>
            </div>
          </div>
          <div v-if="book.customerName" class="cust-picked">
            <i class="mdi mdi-account-check"></i> {{ book.customerName }}
            <button class="secondary small" @click="book.customerId=0; book.customerName=''">Change</button>
          </div>
        </div>
        <p class="muted small" v-if="!book.customerId">…or enter a guest walk-in below.</p>
        <div class="row-2" v-if="!book.customerId">
          <div><label>Guest name</label><input v-model="book.guestName" /></div>
          <div><label>Guest phone</label><input v-model="book.guestPhone" placeholder="0712345678" /></div>
        </div>
        <label>Notes</label>
        <input v-model="book.notes" placeholder="Optional" />
        <div class="modal-actions">
          <button class="secondary" @click="showBook=false">Cancel</button>
          <button class="primary" @click="createAppt" :disabled="booking || !canBook">{{ booking ? 'Booking…' : 'Book' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>

    <!-- APPOINTMENT detail modal -->
    <div v-if="detail" class="modal-back" @click.self="detail=null">
      <div class="modal card">
        <div class="d-head">
          <div>
            <h3>{{ detail.service_name_snapshot }}</h3>
            <p class="muted small">
              {{ time(detail.start_at) }} – {{ time(detail.end_at) }} · {{ detail.provider_name }}<br />
              <i class="mdi mdi-account-outline"></i> {{ detail.customer_name || detail.guest_name || 'Guest' }}
              {{ detail.customer_phone || detail.guest_phone ? '· ' + (detail.customer_phone || detail.guest_phone) : '' }}
            </p>
            <span :class="'appt-pill ' + detail.status">{{ detail.status.replace(/_/g,' ') }}</span>
          </div>
          <button class="secondary" @click="detail=null">Close</button>
        </div>
        <div class="d-kv">
          <div><span>Service price</span><strong>KES {{ format(detail.price) }}</strong></div>
          <div><span>Commission {{ detail.commission_percent }}%</span><strong>KES {{ format(detail.price * detail.commission_percent / 100) }}</strong></div>
        </div>
        <p v-if="detail.notes" class="muted small">Notes: {{ detail.notes }}</p>

        <div class="d-actions">
          <button v-if="detail.status === 'booked'"      class="secondary" @click="advance('checked_in')">Check in</button>
          <button v-if="detail.status === 'checked_in'"  class="secondary" @click="advance('in_progress')">Start</button>
          <button v-if="['booked','checked_in','in_progress'].includes(detail.status)" class="primary" @click="openComplete">Complete &amp; pay</button>
          <button v-if="['booked','checked_in'].includes(detail.status)" class="danger" @click="cancelAppt">Cancel</button>
          <button v-if="detail.status === 'booked'" @click="advance('no_show')" class="secondary">No-show</button>
        </div>
        <p class="err" v-if="detailErr">{{ detailErr }}</p>
        <p class="ok"  v-if="detailOk">{{ detailOk }}</p>
      </div>
    </div>

    <!-- COMPLETE modal -->
    <div v-if="showComplete" class="modal-back" @click.self="showComplete=false">
      <div class="modal card">
        <h3>Complete &amp; take payment</h3>
        <div class="ck-total">Total: <strong>KES {{ format(detail.price) }}</strong></div>
        <label>Payment method</label>
        <select v-model="pay.method">
          <option value="cash">Cash</option>
          <option value="mpesa_till">M-Pesa (manual)</option>
          <option value="card">Card</option>
        </select>
        <div v-if="pay.method === 'cash'">
          <label>Amount tendered</label>
          <input type="number" v-model.number="pay.tendered" />
          <div class="change">Change: KES {{ format(Math.max(0, pay.tendered - detail.price)) }}</div>
        </div>
        <div v-if="pay.method !== 'cash'">
          <label>Reference</label>
          <input v-model="pay.reference" />
        </div>
        <div class="modal-actions">
          <button class="secondary" @click="showComplete=false">Back</button>
          <button class="primary" @click="doComplete" :disabled="completing || !canComplete">{{ completing ? 'Processing…' : 'Confirm payment' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

let custTimer = null;

export default {
  name: 'AppointmentsCalendar',
  components: { Navigation },
  data() {
    return {
      day: new Date().toISOString().slice(0, 10),
      providers: [], services: [], appointments: [],
      showBook: false, booking: false, err: '',
      book: { serviceVariantId: 0, providerUserId: 0, customerId: 0, customerName: '', guestName: '', guestPhone: '', date: '', time: '', notes: '' },
      custSearch: '', custResults: [],
      detail: null, detailErr: '', detailOk: '',
      showComplete: false, completing: false,
      pay: { method: 'cash', tendered: 0, reference: '' },
    };
  },
  computed: {
    slots() {
      // 30-min slots from 08:00 to 20:00
      const out = [];
      for (let h = 8; h < 20; h++) {
        for (const m of [0, 30]) {
          const hh = h.toString().padStart(2, '0');
          const mm = m.toString().padStart(2, '0');
          out.push({ iso: `${hh}:${mm}`, label: `${hh}:${mm}`, hour: h, min: m });
        }
      }
      return out;
    },
    canBook() {
      return this.book.serviceVariantId && this.book.providerUserId && this.book.date && this.book.time
             && (this.book.customerId > 0 || this.book.guestName || this.book.guestPhone);
    },
    canComplete() {
      if (this.pay.method === 'cash') return this.pay.tendered >= (this.detail?.price || 0);
      return !!this.pay.reference;
    }
  },
  async mounted() {
    await Promise.all([this.loadProviders(), this.loadServices(), this.load()]);
  },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    time(iso) { const d = new Date(iso.replace(' ', 'T')); return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`; },
    async load() {
      const { data } = await execute({ function: 'listAppointments', from: this.day, to: this.day }, paysol);
      if (data.success) this.appointments = data.data;
    },
    async loadProviders() {
      const { data } = await execute({ function: 'listProviders' }, paysol);
      if (data.success) this.providers = data.data;
    },
    async loadServices() {
      const { data } = await execute({ function: 'listServices' }, paysol);
      if (data.success) this.services = data.data;
    },
    shiftDay(d) {
      const dt = new Date(this.day); dt.setDate(dt.getDate() + d);
      this.day = dt.toISOString().slice(0, 10); this.load();
    },
    jumpToday() { this.day = new Date().toISOString().slice(0, 10); this.load(); },
    hasAppt(providerId, slot) {
      const slotStart = new Date(`${this.day}T${slot.iso}:00`).getTime();
      return this.appointments.some(a => a.provider_user_id === providerId &&
        new Date(a.start_at.replace(' ', 'T')).getTime() === slotStart);
    },
    apptsFor(providerId, slot) {
      const slotStart = new Date(`${this.day}T${slot.iso}:00`).getTime();
      return this.appointments.filter(a => a.provider_user_id === providerId &&
        new Date(a.start_at.replace(' ', 'T')).getTime() === slotStart);
    },
    apptStyle(a) {
      const start = new Date(a.start_at.replace(' ', 'T')).getTime();
      const end = new Date(a.end_at.replace(' ', 'T')).getTime();
      const mins = Math.max(30, (end - start) / 60000);
      const rows = Math.ceil(mins / 30);
      // Absolute-positioned so it visually spans multiple slot cells
      return { height: `${rows * 60 - 8}px`, background: a.calendar_color || 'var(--paysol-teal-glow)' };
    },
    onSlotClick(providerId, slot) {
      if (this.hasAppt(providerId, slot)) return;   // guarded by click.stop on the block
      this.openBook(providerId, slot);
    },
    openBook(providerId, slot) {
      this.book = {
        serviceVariantId: 0,
        providerUserId: providerId || (this.providers[0]?.id || 0),
        customerId: 0, customerName: '', guestName: '', guestPhone: '',
        date: this.day,
        time: slot ? slot.iso : new Date().toTimeString().slice(0, 5),
        notes: '',
      };
      this.err = ''; this.custSearch = ''; this.custResults = [];
      this.showBook = true;
    },
    onCustSearch() {
      clearTimeout(custTimer);
      const q = this.custSearch.trim();
      if (q.length < 2) { this.custResults = []; return; }
      custTimer = setTimeout(async () => {
        const { data } = await execute({ function: 'listCustomers', search: q }, paysol);
        if (data.success) this.custResults = data.data.slice(0, 8);
      }, 300);
    },
    pickCustomer(c) {
      this.book.customerId = c.id;
      this.book.customerName = c.name || 'Unnamed';
      this.custResults = []; this.custSearch = '';
    },
    async createAppt() {
      this.booking = true; this.err = '';
      try {
        const startAt = `${this.book.date} ${this.book.time}:00`;
        const { data } = await execute({
          function: 'createAppointment',
          serviceVariantId: this.book.serviceVariantId,
          providerUserId: this.book.providerUserId,
          customerId: this.book.customerId,
          guestName: this.book.guestName, guestPhone: this.book.guestPhone,
          startAt, notes: this.book.notes,
        }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showBook = false;
        await this.load();
      } finally { this.booking = false; }
    },
    async openAppt(a) {
      const { data } = await execute({ function: 'getAppointment', id: a.id }, paysol);
      if (data.success) { this.detail = data.data; this.detailErr = ''; this.detailOk = ''; }
    },
    async advance(newStatus) {
      const { data } = await execute({ function: 'updateAppointmentStatus', id: this.detail.id, status: newStatus }, paysol);
      if (data.success) { this.detail.status = newStatus; await this.load(); }
      else this.detailErr = data.message;
    },
    async cancelAppt() {
      const reason = prompt('Cancel reason?');
      if (reason === null) return;
      const { data } = await execute({ function: 'updateAppointmentStatus', id: this.detail.id, status: 'cancelled', reason }, paysol);
      if (data.success) { this.detail = null; await this.load(); }
      else this.detailErr = data.message;
    },
    openComplete() {
      this.pay = { method: 'cash', tendered: Number(this.detail.price), reference: '' };
      this.showComplete = true;
    },
    async doComplete() {
      this.completing = true; this.detailErr = '';
      const payment = { method: this.pay.method, amount: Number(this.detail.price) };
      if (this.pay.method === 'cash') payment.amount = this.pay.tendered;
      else { payment.reference = this.pay.reference; if (this.pay.method === 'mpesa_till') payment.mpesaReceipt = this.pay.reference; }
      try {
        const { data } = await execute({
          function: 'completeAppointment', id: this.detail.id,
          payments: [payment], addOnItems: [],
        }, paysol);
        if (!data.success) { this.detailErr = data.message; return; }
        this.detailOk = `Paid. Receipt: ${data.data.receiptNo} · Commission: KES ${this.format(data.data.commission)}`;
        this.showComplete = false;
        await this.load();
        setTimeout(() => this.detail = null, 2000);
      } finally { this.completing = false; }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1400px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; max-width: 600px; }
.head-actions { display: flex; gap: 6px; align-items: center; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; }
button.small { padding: 4px 8px; font-size: 11px; }

.calendar { padding: 0; overflow: hidden; }
.cal-grid { display: grid; grid-auto-rows: 60px; }
.cal-header {
  position: sticky; top: 0; z-index: 2;
  background: var(--paysol-bg-elev); padding: 10px 12px;
  border-bottom: 1px solid var(--paysol-line); border-right: 1px solid var(--paysol-line);
  display: flex; flex-direction: column; justify-content: center;
}
.prov-name { font-weight: 700; color: var(--paysol-text); font-size: 13px; }
.prov-comm { margin-top: 2px; }
.cal-time { color: var(--paysol-muted); font-size: 11px; padding: 6px 8px; border-bottom: 1px dashed var(--paysol-line); border-right: 1px solid var(--paysol-line); text-align: right; }
.cal-cell { border-bottom: 1px dashed var(--paysol-line); border-right: 1px solid var(--paysol-line); cursor: pointer; position: relative; padding: 4px; }
.cal-cell:hover:not(.has-appt) { background: var(--paysol-teal-glow); }
.appt-block {
  position: absolute; left: 4px; right: 4px; top: 4px; z-index: 1;
  background: var(--paysol-teal-glow); border: 1px solid var(--paysol-teal);
  border-radius: 4px; padding: 4px 6px; overflow: hidden; cursor: pointer;
  display: flex; flex-direction: column; gap: 2px;
}
.appt-block:hover { z-index: 3; box-shadow: 0 4px 12px rgba(0,0,0,.15); }
.appt-block.st-checked_in { border-color: #4FC3F7; background: rgba(79,195,247,.15); }
.appt-block.st-in_progress { border-color: var(--paysol-warning); background: rgba(245,158,11,.15); }
.appt-block.st-completed { border-color: var(--paysol-success); background: rgba(16,185,129,.15); opacity: 0.7; }
.appt-block.st-cancelled, .appt-block.st-no_show { border-color: var(--paysol-muted); background: transparent; opacity: 0.5; text-decoration: line-through; }
.appt-head { display: flex; justify-content: space-between; align-items: center; gap: 4px; }
.appt-time { color: var(--paysol-text); font-weight: 600; font-size: 11px; }
.appt-service { color: var(--paysol-text); font-weight: 600; font-size: 12px; line-height: 1.2; }
.appt-cust { display: flex; align-items: center; gap: 3px; }
.appt-pill { display: inline-block; padding: 1px 5px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
.appt-pill.booked      { background: rgba(139,164,196,.2); color: var(--paysol-muted); }
.appt-pill.checked_in  { background: rgba(79,195,247,.2); color: #4FC3F7; }
.appt-pill.in_progress { background: rgba(245,158,11,.2); color: var(--paysol-warning); }
.appt-pill.completed   { background: rgba(16,185,129,.2); color: var(--paysol-success); }
.appt-pill.cancelled, .appt-pill.no_show { background: rgba(239,68,68,.2); color: var(--paysol-danger); }

.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 500px; max-height: 90vh; overflow-y: auto; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.ok  { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
.cust-picker { position: relative; }
.cust-results { position: absolute; z-index: 5; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 6px; margin-top: 4px; max-height: 220px; overflow-y: auto; width: 100%; box-shadow: 0 8px 20px rgba(0,0,0,.15); }
.cust-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--paysol-line); }
.cust-result:hover { background: var(--paysol-teal-glow); }
.cust-picked { display: flex; align-items: center; gap: 8px; margin-top: 6px; padding: 8px 12px; background: var(--paysol-teal-glow); border-radius: 6px; color: var(--paysol-teal); font-weight: 500; }
.cust-picked button { margin-left: auto; }
.d-head { display: flex; justify-content: space-between; align-items: start; margin-bottom: 14px; }
.d-head h3 { color: var(--paysol-teal); margin: 0 0 4px; }
.d-kv { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding: 12px; background: var(--paysol-bg-elev); border-radius: 6px; margin-bottom: 12px; }
.d-kv span { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; display: block; }
.d-kv strong { color: var(--paysol-text); font-size: 15px; }
.d-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.ck-total { color: var(--paysol-teal); font-size: 22px; text-align: center; padding: 12px 0 16px; }
.change { color: var(--paysol-success); font-weight: 700; margin-top: 6px; }
</style>
